import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { postDataAPI, getDataAPI } from '../../utils/fetchData';
import { setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import LayoutHeader from '../../layout/LayoutHeader';
import { canManageTeam, isDelegatedUser } from '../../utils/permissions';
import { isTourActive } from '../../utils/interactiveTour';
import AppOnboarding from '../../components/AppOnboarding';

const TeamManagementApp = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('manager');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [loadingRemove, setLoadingRemove] = useState(false);
  const [confirmDeleteEmail, setConfirmDeleteEmail] = useState('');
  const [activeTab, setActiveTab] = useState('home');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userArticle } = useSelector((state) => state.user);

  // App color - matches the brand color or your preference
  const appColor = '#8b5cf6'; // Purple, change this to match your team app color

  // Role options
  const roleOptions = [
    {
      value: 'manager',
      label: 'Manager',
      description: 'Full-featured team member with customizable permissions. Manage the artist\'s career, handle business affairs, and coordinate with other team members.'
    },
    {
      value: 'support',
      label: 'Support',
      description: 'View-only access to selected apps. Perfect for assistants, interns, or team members who need limited access to help with specific tasks.'
    }
  ];

  const selectedRoleOption = roleOptions.find(role => role.value === selectedRole);

  // Check user context
  const selectedArtist = localStorage.getItem('selectedArtist');
  const localStorageUser = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
  const isActualArtist = userArticle?.professionRole === 'artist' && !selectedArtist;
  const isTeamMemberDelegated = selectedArtist && ['manager', 'support'].includes(localStorageUser?.professionRole);
  const showTeamManagement = isActualArtist || isTeamMemberDelegated;
  const canManageTeamPermission = canManageTeam();

  // Load team members on mount in background
  useEffect(() => {
    if (activeTab !== 'home') {
      fetchTeamMembers();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'home') {
      fetchTeamMembers();
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.role-dropdown')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const fetchTeamMembers = async () => {
    if (activeTab === 'home') {
      return; // Don't load team members on home tab
    }
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await postDataAPI('team/members', {
        userId: userArticle?._id,
        userRole: userArticle?.professionRole
      }, token);

      if (response.data?.status === 200) {
        setTeamMembers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      if (activeTab !== 'home') {
        dispatch(setErrorAlert('Failed to load team members'));
      }
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) {
      dispatch(setErrorAlert('Please enter an email address'));
      return;
    }

    if (!isValidEmail(email)) {
      dispatch(setErrorAlert('Please enter a valid email address'));
      return;
    }

    setLoadingInvite(true);
    
    const timeoutId = setTimeout(() => {
      setLoadingInvite(false);
      dispatch(setErrorAlert('Request timed out. Please try again.'));
    }, 30000);

    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await postDataAPI('team/invite', {
        email: email.trim(),
        role: selectedRole,
        accountType: selectedRole,
        inviterRole: userArticle?.professionRole,
        inviterId: userArticle?._id,
        artistId: isActualArtist ? userArticle?._id : userArticle?.managedArtist?._id
      }, token);

      clearTimeout(timeoutId);

      if (response.data?.status === 200) {
        dispatch(setSuccessAlert('Team invitation sent successfully!'));
        setEmail('');
        setSelectedRole('manager');
        fetchTeamMembers();
      } else {
        dispatch(setErrorAlert(response.data?.message || 'Failed to send invitation'));
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error sending invitation:', error);
      dispatch(setErrorAlert('Failed to send invitation'));
    } finally {
      setLoadingInvite(false);
    }
  };

  const handleRemoveMember = async (memberEmail) => {
    if (!isActualArtist) {
      dispatch(setErrorAlert('Only artists can remove team members'));
      return;
    }

    setLoadingRemove(true);
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await postDataAPI('team/remove-member', {
        memberEmail: memberEmail,
        artistId: userArticle?._id
      }, token);

      if (response.data?.status === 200) {
        dispatch(setSuccessAlert('Team member removed successfully'));
        setConfirmDeleteEmail('');
        fetchTeamMembers();
      } else {
        dispatch(setErrorAlert(response.data?.message || 'Failed to remove team member'));
      }
    } catch (error) {
      console.error('Error removing team member:', error);
      dispatch(setErrorAlert('Failed to remove team member'));
    } finally {
      setLoadingRemove(false);
    }
  };

  const handleCancelInvitation = async (invitedEmail) => {
    if (!isActualArtist) {
      dispatch(setErrorAlert('Only artists can cancel invitations'));
      return;
    }

    setLoadingRemove(true);
    try {
      const token = localStorage.getItem('dizeeToken');
      const response = await postDataAPI('team/cancel-invitation', {
        invitedEmail: invitedEmail,
        artistId: userArticle?._id
      }, token);

      if (response.data?.status === 200) {
        dispatch(setSuccessAlert('Invitation cancelled successfully'));
        setConfirmDeleteEmail('');
        fetchTeamMembers();
      } else {
        dispatch(setErrorAlert(response.data?.message || 'Failed to cancel invitation'));
      }
    } catch (error) {
      console.error('Error canceling invitation:', error);
      dispatch(setErrorAlert('Failed to cancel invitation'));
    } finally {
      setLoadingRemove(false);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleDisplayName = (role, status) => {
    if (status === 'pending') return 'Pending';
    
    switch (role) {
      case 'artist': return 'Artist (Owner)';
      case 'manager': return 'Manager';
      case 'support': return 'Support';
      default: return 'Team Member';
    }
  };

  const activeMembers = teamMembers.filter(member => member.status === 'active');
  const pendingInvitations = teamMembers.filter(member => member.status === 'pending');

  const tabs = [
    { id: 'home', label: 'Home' },
    { id: 'invite', label: 'Invite' },
    { id: 'manage', label: 'Manage' }
  ];

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Team Management</span>
            <div className="flex items-center gap-4">
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => {
                  // In tour mode, always go back to home to preserve tour state
                  if (isTourActive()) {
                    navigate('/');
                  } else {
                    const canGoBack = window.history.length > 1;
                    if (canGoBack) {
                      navigate(-1);
                    } else {
                      navigate('/');
                    }
                  }
                }}
              >
                Go back
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6 px-4">
            <div className="flex w-full">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ backgroundColor: appColor }}></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {activeTab === 'home' ? (
              <div className="flex flex-col gap-4 mb-8">
                {/* Onboarding - shows when no team members */}
                {teamMembers.length === 0 && (
                  <div className="pt-4">
                    <AppOnboarding
                      appName="Teams"
                      description="Invite your team to collaborate. Set permissions and manage access to your music business."
                      color="#8B5CF6"
                      steps={[
                        {
                          id: 'invite-member',
                          title: 'Invite your first team member',
                          description: 'Add a manager or collaborator',
                          completed: false,
                          onClick: () => setActiveTab('invite')
                        },
                        {
                          id: 'set-permissions',
                          title: 'Set permissions',
                          description: 'Control what they can access',
                          completed: false,
                          onClick: () => setActiveTab('invite')
                        }
                      ]}
                      actionText="Invite Team Member"
                      onAction={() => setActiveTab('invite')}
                      icon={
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      }
                    />
                  </div>
                )}
                
                <div className="mb-4 pt-8">
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5] mb-6">
                    Build and manage your team. Invite managers and collaborators, control permissions, and coordinate your music career together.
                  </p>
                </div>

                <div
                  onClick={() => isActualArtist && setActiveTab('invite')}
                  className={`bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 transition-all ${
                    isActualArtist ? 'hover:border-[#8B5CF6] hover:bg-[rgba(139,92,246,0.05)] cursor-pointer' : ''
                  }`}
                >
                  <h3 className="text-white text-[12px] font-normal mb-2">Invite Team Members</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Invite managers and support staff to help manage your career. Choose between Manager or Support roles with customizable permissions.
                  </p>
                </div>

                <div
                  onClick={() => setActiveTab('manage')}
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#8B5CF6] hover:bg-[rgba(139,92,246,0.05)] transition-all cursor-pointer"
                >
                  <h3 className="text-white text-[12px] font-normal mb-2">Manage Permissions</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Control what each team member can access. Edit permissions, change roles, and manage app access for each team member.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">Role Types</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    <strong>Manager:</strong> Full-featured access with customizable permissions. Perfect for career managers and business partners.<br/><br/>
                    <strong>Support:</strong> View-only access to selected apps. Ideal for assistants, interns, or team members who need limited access.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">Team Collaboration</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Work together seamlessly. Team members can access shared data, manage bookings, handle releases, and coordinate tours all in one place.
                  </p>
                </div>
              </div>
            ) : activeTab === 'invite' ? (
              <>
                {/* Invite Section - Only for Actual Artists */}
                {isActualArtist ? (
                  <div className="mb-6">
                    <div className="text-white text-[12px] font-semibold mb-3">
                      Invite Team Members
                    </div>
                    <div className="flex flex-col gap-y-3">
                      <div className='flex w-full items-center gap-x-2'>
                        <input
                          type="email"
                          placeholder="Enter team member email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="flex-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none transition-colors"
                          style={{
                            borderColor: 'rgba(255,255,255,0.1)',
                          }}
                          onFocus={(e) => e.target.style.borderColor = appColor}
                          onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                        />
                        <button 
                          onClick={handleInvite}
                          disabled={loadingInvite}
                          className="px-4 py-2 rounded-lg text-white text-[11px] font-medium transition-opacity hover:opacity-80"
                          style={{ backgroundColor: appColor }}
                        >
                          {loadingInvite ? 'Inviting...' : 'Invite'}
                        </button>
                      </div>
                      
                      {/* Role Selection Dropdown */}
                      <div className="flex flex-col gap-y-2">
                        <div className="text-white text-[11px] font-medium">Select Role:</div>
                        <div className="relative role-dropdown">
                          <div 
                            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center text-white text-[12px] transition-all duration-200"
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                          >
                            <span>{selectedRoleOption ? selectedRoleOption.label : "Select role..."}</span>
                            <span 
                              className="text-[rgba(255,255,255,0.7)] text-[10px] transform transition-transform duration-200"
                              style={{
                                transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                              }}
                            >
                              ▼
                            </span>
                          </div>
                          
                          {dropdownOpen && (
                            <div
                              className="absolute top-full left-0 right-0 bg-[#111111] rounded-lg mt-1 z-50 overflow-hidden shadow-lg"
                            >
                              <div className="h-2 bg-[#111111]"></div>
                              
                              <div className="px-2 flex flex-col gap-y-2">
                                {roleOptions.map((role) => (
                                  <div
                                    key={role.value}
                                    className="flex flex-col gap-y-1 cursor-pointer p-3 rounded transition-all duration-200 border"
                                    style={{
                                      borderColor: selectedRole === role.value ? appColor : 'transparent'
                                    }}
                                    onClick={() => {
                                      setSelectedRole(role.value);
                                      setDropdownOpen(false);
                                    }}
                                    onMouseEnter={(e) => {
                                      if (selectedRole !== role.value) {
                                        e.currentTarget.style.borderColor = appColor;
                                        e.currentTarget.style.opacity = '0.7';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (selectedRole !== role.value) {
                                        e.currentTarget.style.borderColor = 'transparent';
                                        e.currentTarget.style.opacity = '1';
                                      }
                                    }}
                                  >
                                    <div className="flex items-center gap-2 text-white text-[12px] font-semibold">
                                      {role.label}
                                      {selectedRole === role.value && (
                                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                    <div className="text-white/70 text-[10px] leading-[1.3]">
                                      {role.description}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              <div className="h-2 bg-[#111111]"></div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-[rgba(255,255,255,0.6)] text-[11px] leading-[1.4]">
                        The selected role will determine what access this team member has to your account.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-6 p-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg">
                    <div className="text-white text-[11px]">
                      Only artists can invite team members. Contact the account owner to add team members.
                    </div>
                  </div>
                )}

                {/* Pending Invitations Section */}
                {pendingInvitations.length > 0 && (
                  <>
                    <div className="h-px bg-[rgba(255,255,255,0.1)] my-6"></div>
                    <div className="mb-6">
                      <div className="text-white text-[12px] font-semibold mb-3">
                        Pending Invitations ({pendingInvitations.length})
                      </div>
                      <div className="space-y-3">
                        {pendingInvitations.map((invitation, index) => (
                          <div 
                            key={index} 
                            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 opacity-70 transition-all"
                            onMouseEnter={(e) => e.currentTarget.style.borderColor = appColor}
                            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="text-white text-[13px] font-normal mb-0.5">
                                  {invitation.email}
                                </div>
                                <div className="text-[rgba(255,255,255,0.8)] text-[11px]">
                                  {getRoleDisplayName(invitation.role, 'pending')} • Invited {new Date(invitation.invitedAt).toLocaleDateString()}
                                </div>
                                {invitation.expiresAt && (
                                  <div className="text-[rgba(255,255,255,0.5)] text-[10px] mt-1">
                                    Expires {new Date(invitation.expiresAt).toLocaleDateString()}
                                  </div>
                                )}
                              </div>

                              {isActualArtist ? (
                                confirmDeleteEmail === invitation.email ? (
                                  <button 
                                    onClick={() => handleCancelInvitation(invitation.email)}
                                    className="text-red-400 text-[11px] hover:opacity-70 transition-opacity"
                                    disabled={loadingRemove}
                                  >
                                    {loadingRemove ? 'Cancelling...' : 'Confirm'}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setConfirmDeleteEmail(invitation.email)}
                                    className="text-red-400 text-[11px] hover:opacity-70 transition-opacity"
                                  >
                                    Cancel
                                  </button>
                                )
                              ) : (
                                <span className="text-[rgba(255,255,255,0.6)] text-[11px]">
                                  Pending
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="text-[rgba(255,255,255,0.6)] text-[11px] leading-[1.4] mt-3">
                        Pending invitations will automatically move to "Active Team Members" when the invited person accepts and completes their signup.
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : activeTab === 'manage' ? (
              <>
                {/* View-Only Notice for Team Members */}
                {isTeamMemberDelegated && (
                  <div className="mb-6 p-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg">
                    <div className="text-white text-[11px]">
                      Team Member View: You can view the team members but only the artist can edit permissions.
                    </div>
                  </div>
                )}

                {!showTeamManagement && (
                  <div className="mb-6 p-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg">
                    <div className="text-white text-[11px]">
                      Manager View: You can view team members but only the artist can edit permissions.
                    </div>
                  </div>
                )}

                {/* Active Team Members Section */}
                {activeMembers.length > 0 ? (
                  <div className="mb-6">
                    <div className="text-white text-[12px] font-semibold mb-3">
                      Active Team Members ({activeMembers.length})
                    </div>

                    <div className="space-y-3">
                      {activeMembers.map((member, index) => (
                        <div 
                          key={index} 
                          className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 transition-all"
                          style={{
                            transition: 'border-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.borderColor = appColor}
                          onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-[12px] overflow-hidden flex-shrink-0"
                              style={{ 
                                background: `linear-gradient(45deg, ${appColor}, #a78bfa)`
                              }}
                            >
                              {member.profilePicture ? (
                                <img 
                                  src={member.profilePicture} 
                                  alt={member.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                getInitials(member.name || member.email)
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="text-white text-[13px] font-normal mb-0.5">
                                {member.name || member.email || 'Unnamed User'}
                              </div>
                              {member.name && member.name !== member.email && (
                                <div className="text-[rgba(255,255,255,0.6)] text-[11px] mb-0.5">
                                  {member.email}
                                </div>
                              )}
                              <div 
                                className="text-[10px] px-2 py-0.5 rounded inline-block"
                                style={{
                                  backgroundColor: `${appColor}20`,
                                  color: appColor
                                }}
                              >
                                {getRoleDisplayName(member.role, member.status)}
                              </div>
                            </div>

                            {isActualArtist && member.role !== 'artist' && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => navigate(`/team-management/edit/${member._id}`)}
                                  className="text-white text-[11px] hover:opacity-70 transition-opacity"
                                >
                                  Edit
                                </button>
                                {confirmDeleteEmail === member.email ? (
                                  <button 
                                    onClick={() => handleRemoveMember(member.email)}
                                    className="text-red-400 text-[11px] hover:opacity-70 transition-opacity"
                                    disabled={loadingRemove}
                                  >
                                    {loadingRemove ? 'Removing...' : 'Confirm'}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setConfirmDeleteEmail(member.email)}
                                    className="text-red-400 text-[11px] hover:opacity-70 transition-opacity"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            )}
                            
                            {!isActualArtist && member.role === 'artist' && (
                              <span className="text-[rgba(255,255,255,0.6)] text-[11px]">
                                Owner
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-left py-8">
                    <div className="text-white text-[12px] font-semibold mb-2">
                      No team members yet.
                    </div>
                    {canManageTeamPermission && (
                      <p className="text-[rgba(255,255,255,0.6)] text-[11px] leading-[1.5]">
                        Invite managers and collaborators to help grow your presence on Dizee.
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default TeamManagementApp;

